/**
 * Renderização única dos checkboxes de consentimento (LGPD) do
 * `OfertaFormularioModule`.
 *
 * Antes existiam duas cópias divergentes de `renderCheckboxes` — uma em
 * `ofertaFormularioMultiStepRenderer.js` (consumida também pelo renderer
 * `NoCepOferta`) e outra em `ofertaFormularioRenderer.js` (modelo `padrao`).
 * Este módulo é a **única** definição, consumida pelos três renderers, para que
 * o contrato de erro por opt-in (AC-9) não precise ser aplicado três vezes.
 */

/**
 * Metadados de cada checkbox de consentimento.
 * `fieldId` é o sufixo usado nos ids do DOM (`#${cssPrefix}-${fieldId}` no
 * controle e `#${cssPrefix}-${fieldId}-error` na mensagem) — é a chave que
 * `formValidationUi.js` usa para marcar erro/ARIA por checkbox.
 * @type {{name: string, fieldId: string}[]}
 */
export const OPTIN_CHECKBOX_FIELDS = [
    { name: 'opt_in_widget', fieldId: 'opt-in-widget' },
    { name: 'opt_in_aceite', fieldId: 'aceite' },
    { name: 'opt_in_ciente', fieldId: 'ciente' },
    { name: 'opt_in_declaro', fieldId: 'declaro' },
];

/**
 * Ordem visual dos ids de erro dos opt-ins, para compor a ordem de foco dos
 * renderers (`focusFirstInvalidField`). Só os checkboxes efetivamente
 * renderizados terão div de erro preenchida, então entradas ausentes no DOM são
 * simplesmente ignoradas.
 * @type {readonly string[]}
 */
export const OPTIN_FIELD_ORDER = OPTIN_CHECKBOX_FIELDS.map((item) => item.fieldId);

/**
 * Renderiza um checkbox de consentimento + a div de erro pareada.
 * @param {string} cssPrefix
 * @param {string} name Atributo `name` do input (chave de `OPTIN_MESSAGES`).
 * @param {string} fieldId Sufixo dos ids do DOM.
 * @param {string} text Texto legal vindo do CMS (HTML já confiável, não escapado —
 *   mesmo comportamento das duas implementações anteriores).
 * @param {string} suffix Marcador de obrigatoriedade anexado ao texto.
 * @returns {string}
 */
function renderOptinCheckbox(cssPrefix, name, fieldId, text, suffix) {
    return `
            <label>
                <input type="checkbox" required name="${name}" id="${cssPrefix}-${fieldId}" />
                <span>${text}${suffix}</span>
            </label>
            <div id="${cssPrefix}-${fieldId}-error" class="${cssPrefix}-error"></div>
        `;
}

/**
 * Renderiza os checkboxes LGPD do formulário.
 *
 * Quando a oferta define `optin_widget`, exibe apenas esse checkbox único (que
 * vale pelos três); caso contrário exibe os checkboxes de aceite/ciente/declaro
 * cujos textos existirem.
 *
 * @param {string} cssPrefix
 * @param {boolean} hasOptInWidget
 * @param {string} textAceite
 * @param {string} textCiente
 * @param {string} textDeclaro
 * @param {string} optinWidgetText
 * @param {{requiredMark?: boolean}} [options] `requiredMark` (padrão `true`)
 *   anexa " *" ao texto — o modelo `padrao` passa `false` porque o seu layout
 *   não exibe a nota "Campos marcados com * são obrigatórios".
 * @returns {string} HTML dos checkboxes, cada um seguido da sua div de erro.
 */
export function renderCheckboxes(
    cssPrefix,
    hasOptInWidget,
    textAceite,
    textCiente,
    textDeclaro,
    optinWidgetText,
    options = {}
) {
    const { requiredMark = true } = options;
    const suffix = requiredMark ? ' *' : '';

    if (hasOptInWidget && optinWidgetText) {
        return renderOptinCheckbox(cssPrefix, 'opt_in_widget', 'opt-in-widget', optinWidgetText, suffix);
    }

    let html = '';

    if (textAceite) {
        html += renderOptinCheckbox(cssPrefix, 'opt_in_aceite', 'aceite', textAceite, suffix);
    }

    if (textCiente) {
        html += renderOptinCheckbox(cssPrefix, 'opt_in_ciente', 'ciente', textCiente, suffix);
    }

    if (textDeclaro) {
        html += renderOptinCheckbox(cssPrefix, 'opt_in_declaro', 'declaro', textDeclaro, suffix);
    }

    return html;
}
